/**
 * SAP CONFIDENTIAL
 *
 * This software is confidential proprietary software licensed by SAP SE
 * and is not to be used, duplicated or disclosed to anyone without the
 * prior written permission of SAP.
 *
 * (C) 2020 Copyright SAP SE.
 */

import { map } from 'rxjs/operators';
import { Output, EventEmitter, Injectable } from '@angular/core';
import { Subject ,  Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { FEATURES_CODES, COMPANY_TRANSLATION_CODES, LOCALE } from '../../config/constants';
import moment from 'moment-timezone';
import { Router } from '@angular/router';
import * as _ from 'underscore';

@Injectable()
export class CommonService {
  @Output() menuClicked: EventEmitter<any> = new EventEmitter();
  @Output() panelBtnClicked: EventEmitter<any> = new EventEmitter();
  @Output() closePanelBtnClicked: EventEmitter<any> = new EventEmitter();
  @Output() statusPanelBtnClicked: EventEmitter<any> = new EventEmitter();
  @Output() bookmarkPanelClosed: EventEmitter<any> = new EventEmitter();
  private subject = new Subject<any>();
  private transSubject = new Subject<any>();
  private hierarchySubject = new Subject<any>();
  public subscription = new Subject();
  public filterChange = new Subject<any>();
  public columnChange = new Subject<any>();
  public fMenu: any = false;
  private changeSponsorState: any = new Subject<any>();
  private changeRecruiterState: any = new Subject<any>();
  private changeOrganizationUnit: any = new Subject<any>();
  private changeParentOrganizationUnit: any = new Subject<any>();
  public selectedLan: any = new Subject<any>();
  private url: string = environment.host;
  private fetchGroupUrl: string = environment.host + '/group/';
  private planUrl: string = environment.host + '/plan';
  public distDetails: any = '';
  public distData = new EventEmitter<any>();
  public userDetails: any;
  public reprocessFlag = false;
  public reportTagURL = environment.host + '/report/tag';
  private allColumnSets: any = [];
  private allColumnSetsName: any = [];
  private selectedColumns: any = [];
  private reportsUrl: string = environment.host + '/user/report';
  private allFilters: any = [];
  private selectedFilters: any = [];
  private selectedReport: any = {};
  private timeZoneInfo: any;
  public helpObj: any;
  private globalMetaUrl: string = environment.host + '/globalMetadata';
  private translationsUrl: string = environment.host + '/translation';
  private translationsAllUrl: string = environment.host + '/translations';
  private calendarUrl: string = environment.host + '/calendarDefinition?companyCode=';
  private helpUrl: string = environment.host + '/help/';
  public updateFieldLookupSearch: Observable<any>;
  public caseNumber: Subject<any>;
  public getGlobalSettingsUrl: string = environment.host + '/globalSettings';
  public getGlobalSettingsUrlTabType: string = environment.host + '/globalSettings/login';
  public globalDateFormat: any;
  public static localeCode: string;
  public static localeCurrency: string;
  public licenseUrl: string = environment.host + '/license/agGridLicense';
  public validUserUrl: string = environment.host + '/user/validate';
  public ActivatedSideSubMenu!: string;
  public translatedJSON: any;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private toastr: ToastrService,
    private router: Router
  ) {
    this.caseNumber = new Subject<boolean>();
    this.updateFieldLookupSearch = this.caseNumber.asObservable();
  }

  public setLocale(code: string, currency: string){
    CommonService.localeCode = code;
    CommonService.localeCurrency = currency;
  }

  public getCalendarDefinition(companyCode: any) {
    const calUrl = this.calendarUrl + companyCode;
    return this.http.get(calUrl).toPromise();
  }

  public getCalendarDefinitionTabType(companyCode: any) {
    const calUrl = this.calendarUrl + companyCode + '&tabType=planperiodview/homepage';
    return this.http.get(calUrl).toPromise();
  }

  publishData(Data: any) {
    this.caseNumber.next(Data);
  }

  public setTimeZonInfo(timeZoneInfo: any) {
    this.timeZoneInfo = timeZoneInfo;
  }

  public getTimeZonInfo() {
    return localStorage.getItem('calendarBasedTimeZone');
  }

  public sendPanelState(state: any) {
    this.subject.next(state);
  }

  public getTransState(): Observable<any> {
    return this.transSubject.asObservable();
  }

  public setTransState(state: any) {
    this.transSubject.next(state);
  }

  public getPanelState(): Observable<any> {
    return this.subject.asObservable();
  }

  public fetchGlobalMetaData() {
    return this.http.get(this.globalMetaUrl).toPromise();
  }

  public getAllGroups() {
    return this.http.get(this.fetchGroupUrl).toPromise();
  }

  public getGlobalSettings(payload: any) {
    if (payload) {
      const auth = btoa(payload.username + ':' + payload.password),
        headers = { Authorization: 'Basic ' + auth },
        code = btoa(payload.code);
      return this.http.get(this.getGlobalSettingsUrl + `?code=${code}`, { headers: headers }).toPromise();
    } else {
      return this.http.get(this.getGlobalSettingsUrl).toPromise();
    }
  }

  public getGlobalSettingstabType(payload: any) {
    if (payload) {
      const auth = btoa(payload.username + ':' + payload.password),
        headers = { Authorization: 'Basic ' + auth },
        code = btoa(payload.code);
      return this.http.get(this.getGlobalSettingsUrlTabType + `?code=${code}`, { headers: headers }).toPromise();
    } else {
      return this.http.get(this.getGlobalSettingsUrlTabType).toPromise();
    }
  }

  public setDateFormat(dateFormat: any) {
    this.globalDateFormat = dateFormat;
    localStorage.setItem('globalDateFormat', dateFormat);
  }

  public getDateFormat() {
    return localStorage.getItem('globalDateFormat') || 'MM/DD/YYYY';
  }

  public getErrorMessage(errorObj: any) {
    const parsedErrorObj = {
      headerMsg: '',
      errorMsg: ''
    };
    let headerMsg = '',
      errors = [],
      errorMsg = '';
    const tempErrorObj = errorObj ? (typeof errorObj === 'string' ? JSON.parse(errorObj) : errorObj) : '';
    if (tempErrorObj) {
      const errorBody = tempErrorObj.error ? (typeof tempErrorObj.error === 'string' ? JSON.parse(tempErrorObj.error) : tempErrorObj.error) : '';
      if (errorBody) {
        headerMsg = errorBody.errorCode;
        errors = errorBody.errors;
        errorMsg = errorBody.errorMessage || '';
      }

      for (const error in errors) {
        errorMsg +=
          ' <br> ' +
          (errors[error].property || ' Property field not available') +
          ': ' +
          (errors[error].errorMessage || 'Error message not available') +
          ' <br> ';
      }
    }

    parsedErrorObj.headerMsg = headerMsg || 'Error';
    parsedErrorObj.errorMsg = errorMsg || 'Something went wrong.';
    if (errorObj.status === 401) {
      parsedErrorObj.headerMsg = 'Error';
      parsedErrorObj.errorMsg = errorMsg || 'Authentication Failed';
    }
    return `<div class="clearfix"><div class="pull-left col-sm-11"><h4 class="no-margin">${parsedErrorObj.headerMsg}</h3><p class="no-margin">${parsedErrorObj.errorMsg}</p></div><i class="icon-message-error cc-notification-icon float-start"></i></div>`;
  }

  public makePrevStepVisited(wizardSteps: Array<any>, toTab: string, action: string) {
    const selectedTabIndex = wizardSteps.findIndex(function (item) {
      return item.tab === toTab;
    });

    if (selectedTabIndex) {
      wizardSteps[selectedTabIndex].visited = true;
      if (action === 'Next') {
        wizardSteps[selectedTabIndex - 1].visited = true
      }
    }
  }

  public hasRequiredError(formControls: any = {}) {
    for (const key in formControls) {
      if ({}.hasOwnProperty.call(formControls, key) && formControls[key].hasError('required')) {
        return true;
      }
    }
    return false;
  }

  public closeDDSliders() {
    this.subscription.next();
  }

  public getVolumeDefinitionsByPlanId(planId: number) {
    const plan = this.planUrl.toString() + '/' + planId + '/volume-definitions';
    return this.http.get(plan).toPromise();
  }

  public convertNumberFloatZero(input: number | string) {
    if (input != '' && input != null && input != undefined) {
      const numberFloat =
          Number(input) < 0
            ? parseFloat((Math.round(Number(input) * 100) / 100).toString()).toFixed(2)
            : Number(input).toFixed(2),
        splitNumber: any = numberFloat.toString().split('.');
      if (Number(splitNumber[0]) == 0) {
        return Number(numberFloat);
      } else {
        return Number(splitNumber[0].toLocaleString(this.translate.currentLang).concat('.').concat(splitNumber[1]));
      }
    } else {
      // fix for 4358 - to handle 0 value
      return typeof input == 'number' ? Number(input) : '';
    }
  }

  public hasFeatureAccess(featureName: any) {
    const featAvailable: any = localStorage.getItem('featuresAvailable');
    const featureCodes: any = FEATURES_CODES;
    const features = JSON.parse(featAvailable);
    return features ? features[featureCodes[featureName]] : false;
  }

  public naviagtePanelPage(panelName: any, clientInfo: any) {
    const panel = {
      name: panelName,
      clientInfo: clientInfo
    };
    this.menuClicked.emit(panel);
  }

  public naviagtePanels(panelName: any) {
    const panel = {
      name: panelName
    };
    this.panelBtnClicked.emit(panel);
  }

  public navigatePanelClose(panelName: any) {
    this.closePanelBtnClicked.emit(panelName);
  }

  public statusPanelClose() {
    this.statusPanelBtnClicked.emit();
  }

  public bookmarkPanelClose() {
    this.bookmarkPanelClosed.emit();
  }

  public setDistributorDetails(distributor: any) {
    this.distDetails = distributor;
  }

  public getDistributorDetails() {
    return this.distDetails;
  }

  public setUserDetails(userDetails: any) {
    this.userDetails = userDetails;
    if (userDetails) {
      localStorage.setItem('userDetails', JSON.stringify(userDetails));
    }
  }

  // set language on update
  public setSelectedLan(lan: any) {
    this.selectedLan.next(lan);
  }

  public getUserDetails() {
    const userDetail: any = localStorage.getItem('userDetails');
    return JSON.parse(userDetail) || null;
  }

  public getCompanyCode() {
    const userDetail: any = localStorage.getItem('userDetails');
    const userDetails = JSON.parse(userDetail);
    if (userDetails && userDetails.company && userDetails.company.code) {
      return userDetails.company.code;
    } else {
      return null;
    }
  }

  public setReprocess(flag: boolean) {
    this.reprocessFlag = flag;
  }

  public getReprocess() {
    return this.reprocessFlag;
  }

  public getGenealogyConfig() {
    const url = environment.host + '/company/user/genealogy/configuration';
    return this.http.get(url).toPromise();
  }

  public getGenealogyConfiguration() {
    const leaderFcn: any = localStorage.getItem('leadershipFunction');
    const leadershipFunction = JSON.parse(leaderFcn);
    return leadershipFunction || { name: '' };
  }

  public setGenealogyConfiguration(result: any) {
    if (result) {
      localStorage.setItem('leadershipFunction', JSON.stringify(result));
    } else {
      localStorage.setItem('leadershipFunction', JSON.stringify({ name: '' }));
    }
  }

  public setCompanyConfiguration(result: any) {
    localStorage.setItem('companyConfiguration', JSON.stringify(result));
  }

  public getCompanyConfiguration() {
    const companyConfig: any = localStorage.getItem('companyConfiguration');
    return JSON.parse(companyConfig) || null;
  }

  public changeDateToViewMs(givenDate: any) {
    const date = this.changeDateToMs(givenDate);
    if (date) {
      return new Date(date);
    }
  }

  public changeDateToMs(givenDate: any): any {
    if (!givenDate) {
      return null;
    }

    const timeZone = this.timeZoneInfo;

    // If already a Date or string → convert directly
    if (givenDate instanceof Date || typeof givenDate === 'string' || typeof givenDate === 'number') {
      return moment.tz(givenDate, timeZone).valueOf();
    }

    // Legacy ngx-mydatepicker style object
    if (givenDate.date) {
      const { year, month, day } = givenDate.date;
      const monthStr = this.pad2(month);
      const dayStr = this.pad2(day);
      const dateStr = `${year}-${monthStr}-${dayStr}${givenDate.time || ''}`;

      return moment.tz(dateStr, timeZone).valueOf();
    }

    return null;
  }

  // convertToMilliseconds(dateValue) {
  //   if (!dateValue) return null;
  //   const dateObj = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
  //   return dateObj instanceof Date && !isNaN(dateObj.getTime()) ? dateObj.getTime() : null;
  // }

  public convertToMilliseconds(dateValue: any): number | null {
    if (!dateValue) return null;

    const dateObj = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;

    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return null;

    const normalized = new Date(Date.UTC(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
      12, 0, 0, 0 
    ));

    return normalized.getTime();
  }

  public convertToMillis(dateValue: any): number | null {
    if (!dateValue) return null;

    const dateObj = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;

    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return null;

    const normalized = new Date(Date.UTC(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
      0, 0, 0, 0 
    ));

    return normalized.getTime();
  }

  public pad2(number: any) {
    return (number < 10 ? '0' : '') + number;
  }

  public prepareUTCFormat(date: any) {
    if (date) {
      return moment(date).utc().format('MM/DD/YYYY');
    }
  }

  public prepareDateObj(dateStr: Date | string | number): any {
    if (dateStr) {
      const timeZone: any = this.getTimeZonInfo();
      const finalDate: any = { date: { month: 0, day: 0, year: 0 }, jsdate: null };
      const mTimezone: moment.Moment = moment.tz(dateStr, timeZone).startOf('day');

      if (mTimezone) {
        finalDate.date.month = mTimezone.month() + 1;
        finalDate.date.day   = mTimezone.date();
        finalDate.date.year  = mTimezone.year();
        finalDate.jsdate     = mTimezone.toDate();
      }
      return finalDate;
    }
    return null;
  }

  public prepareBSDateObj(dateStr: Date | string | number): Date | null {
    if (dateStr) {
      const timeZone: any = this.getTimeZonInfo();
      const mTimezone: moment.Moment = moment.tz(dateStr, timeZone).startOf('day');
      return mTimezone.toDate();
    }
    return null;
  }

  public prepareBSEffectiveDateObj(dateStr: Date | string | number): Date | null {
    if (dateStr) {
      const timeZone: any = this.getTimeZonInfo();
      const mTimezone: moment.Moment = moment.tz(dateStr, timeZone);
      return mTimezone.toDate();
    }
    return null;
  }

  public redirectToDDPage(clientId: any) {
    if (clientId) {
      const url = window.location.origin + '/#/fieldsupport/distributorDetails/' + clientId;
      window.open(url, '_blank');
    }
  }
  
  public redirectToGenealogy(clientId: any, type: any) {
    if (clientId) {
      const genealogyUrl = window.location.origin + '/#/genealogyHome/genealogy/default';
      localStorage.setItem('genealogyParticipantId', clientId);
      localStorage.setItem('lineageType', type);
      window.open(genealogyUrl, '_blank');
    }
  }

  public redirectItem(clientID: string, module: string) {
    let path: any = '';
    switch (module) {
      case 'Genealogy':
        path = '#/genealogyHome/genealogy/default';
        if (clientID) {
          localStorage.setItem('genealogyParticipantId', clientID);
          localStorage.setItem('genealogyGroup', '3');
        }
        break;
      case 'Analytics':
        if (clientID) {
          localStorage.setItem('analyticsParticipantId', clientID);
        }
        path = '#/compensationHome/compensationAnalytics/default';
        break;
      case 'TieredCompensations':
        if (clientID) {
          localStorage.setItem('tieredCompensationsParticipantId', clientID);
        }
        path = '#/compensationHome/tieredCompensations/default';
        break;
      case 'Payments':
        if (clientID) {
          localStorage.setItem('paymentsParticipantId', clientID);
        }
        path = '#/compensationHome/payments/default';
        break;
      case 'DistributorSupport':
        path = '#/fieldsupport/distributorDetails/' + clientID;
        break;
      case 'Orders':
        if (clientID) {
          localStorage.setItem('ordersParticipantId', clientID);
        }
        path = '#/fieldsupport/participantOrders/default';
        break;
      case 'PlannedManagerChanges':
        if (clientID) {
          localStorage.setItem('managerParticipantId', clientID);
        }
        path = '#/compensationHome/plannedManagerChangesReport/default';
        break;
      case 'SalesOrder':
        if (clientID) {
          localStorage.setItem('salesOrderParticipantId', clientID);
        }
        path = '#/compensationHome/salesOrderReport/default';
        break;
      default:
        path = '#/compensationHome/compensationAnalytics/default';
    }
    const routetURL = window.location.origin + path;
    window.open(routetURL, '_blank');
  }

  public getHomeMetaAPIData() {
    const homeMetadataUrl = environment.host + '/home/metadata';
    const headers = new HttpHeaders();
    headers.append('hideLoader', 'true');
    // const options = new RequestOptions({ headers: headers });
    return this.http.get(homeMetadataUrl, { observe: 'body' }).toPromise();
  }

  public getHomeMetaDetails(): { modules: Array<any> } {
    const homeMetaDataStr = localStorage.getItem('homeMetaData');
    if (homeMetaDataStr) {
      return JSON.parse(homeMetaDataStr);
    } else {
      return { modules: [] };
    }
  }

  public setHomeMetaDetails(data: { modules: Array<any> }) {
    localStorage.setItem('homeMetaData', JSON.stringify(data));
  }

  public getBookmarks() {
    const bookmarkUrl = environment.host + '/home/recent';
    return this.http.get(bookmarkUrl).toPromise();
  }

  public getBookmarksTabType(tabType: string = 'userhomePage') {
  const bookmarkUrl = environment.host + '/home/recent/homepage';
  const bookmarkUrl2 = environment.host + '/home/recent/bookmark';
  return this.http.get(
    tabType === 'userhomePage' ? bookmarkUrl : `${bookmarkUrl2}?tabType=${tabType}`
  ).toPromise();
}

  public getAllReportTags() {
    return this.http.get(this.reportTagURL).toPromise();
  }

  public saveReportTags(payload: any) {
    return this.http.post(this.reportTagURL, payload).toPromise();
  }

  public commonErrorHandler(error: any) {
    this.toastr.clear();
    const str: any = this.getErrorMessage(error);
    const url = localStorage.getItem('unAuthorizedURL');
    if (url === null || url === 'null') {
      this.toastr.error(str, '', { enableHtml: true });
      localStorage.removeItem('unAuthorizedURL');
    }
  }

  public clearErrorHandler() {
    this.toastr.clear();
  }

  public removeNotification() {
    this.toastr.clear();
  }

  /* Grid Save, Filter, Column Selector Modals Methods */
  public getAllColumnSets() {
    return this.allColumnSets;
  }

  public setAllColumnSets(columns: any) {
    this.allColumnSets = columns;
    this.columnChange.next();
  }

  public getColumnSetsName() {
    return this.allColumnSetsName;
  }

  public setColumnSetsName(name: any) {
    this.allColumnSetsName = name;
  }

  public getSelectedColumns() {
    return this.selectedColumns;
  }

  public setSelectedColumns(columns: any) {
    this.selectedColumns = columns;
  }

  public getAllReports() {
    return this.http.get(this.reportsUrl).toPromise();
  }

  public createReport(payload: any,reportType?:any) {
    let URL=environment.host + '/user/report';
    if(reportType!=undefined) URL+=`?reportType=${reportType}`;
    return this.http.post(URL, payload).toPromise();
  }

  public createNewReport(reportType?: any) {
    let URL = environment.host + '/create/report';
    if (reportType !== undefined) {
      URL += `?reportType=${reportType}`;
    }
    return this.http.post(URL, null).toPromise();
  }


  public updateReport(payload: any,reportType?:any) {
    if(reportType!==undefined){
      return this.http.put((this.reportsUrl + '/' + payload.id + `?reportType=${reportType}`).toString(), payload).toPromise();
    }
    return this.http.put((this.reportsUrl + '/' + payload.id).toString(), payload).toPromise();
  }

  public deleteReport(reportId: any) {
    return this.http.delete((this.reportsUrl + '/' + reportId).toString()).toPromise();
  }

  public getReportDetails(reportId: any) {
    return this.http.get((this.reportsUrl + '/' + reportId).toString()).toPromise();
  }

  public getAllFilters() {
    return this.allFilters;
  }

  public setAllFilters(items: any) {
    this.allFilters = items;
    this.filterChange.next();
  }

  public setSelectedFilters(filters: any) {
    this.selectedFilters = filters;
  }

  public getSelectedFilters() {
    return this.selectedFilters;
  }

  public getSelectedReport() {
    return this.selectedReport;
  }

  public setSelectedReport(report: any) {
    this.selectedReport = report;
  }

  /* Grid Save, Filter, Column Selector Modals Methods Ends */
  public isValidCSVFiles(files: any) {
    let valid = true;
    for (const file of files) {
      if (!(file?.name?.endsWith('.csv') || file?.name?.endsWith('.CSV'))) {
        valid = false;
        break;
      }
    }
    return valid;
  }

  public isValidTxtFiles(files: any) {
    let valid = true;
    for (const file of files) {
      if (!(file.name.endsWith('.txt') || file.name.endsWith('.TXT'))) {
        valid = false;
        break;
      }
    }
    return valid;
  }

  public getHelpObj() {
    return this.helpObj;
  }

  public setHelpObj(helpObj: any) {
    this.helpObj = helpObj || {};
  }

  public getMenuId(menuCode: any) {
    const menuId: any = localStorage.getItem('menuIds');
    const menuObj = JSON.parse(menuId) || {};
    return menuObj[menuCode];
  }

  public postTranslation(payload: any) {
    return this.http.post(this.translationsUrl, payload).toPromise();
  }

  public getTranslation(lang: any) {
    return this.http.get(this.translationsUrl + '/' + lang).toPromise();
  }

  public getAllTranslations() {
    return this.http.get(this.translationsAllUrl).toPromise();
  }

  public generateRandomPassword(pwdLength: any) {
    const lowerCharacters = [
      'a',
      'b',
      'c',
      'd',
      'e',
      'f',
      'g',
      'h',
      'i',
      'j',
      'k',
      'l',
      'm',
      'n',
      'o',
      'p',
      'q',
      'r',
      's',
      't',
      'u',
      'v',
      'w',
      'x',
      'y',
      'z'
    ];
    const upperCharacters = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z'
    ];
    const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const symbols = ['!', '?', '@'];
    let finalPassword = '';

    const getRandom = function (array: any) {
      return array[Math.floor(Math.random() * array.length)];
    };

    finalPassword = finalPassword.concat(getRandom(upperCharacters));
    finalPassword = finalPassword.concat(getRandom(numbers));
    finalPassword = finalPassword.concat(getRandom(symbols));
    for (let i = 1; i < pwdLength - 3; i++) {
      finalPassword = finalPassword.concat(getRandom(lowerCharacters));
    }

    //shuffle!
    return finalPassword
      .split('')
      .sort(function () {
        return 0.5 - Math.random();
      })
      .join('');
  }

  public getAllProductCategories() {
    const url = environment.host + '/productCategory';
    return this.http.get(url).toPromise();
  }

  public getOnlyIds(arr: any) {
    const response = [];
    if (arr) {
      for (const item of arr) {
        response.push(item.id);
      }
    }
    return response;
  }

  public prepareIds(arr: any) {
    const response = [];
    for (const item of arr) {
      response.push({
        id: item
      });
    }
    return response;
  }

  public getHelpContent(reqObj: any) {
    const url = this.helpUrl + '?menuId=' + reqObj.menuId + '&tab=' + (reqObj.tab || '');
    return this.http.get(url.toString()).toPromise();
  }

  public saveHelpContent(payload: any) {
    return this.http.post(this.helpUrl, payload).toPromise();
  }

  public getFieldValue(options: any, compareField: any, givenValue: any, returnField: any) {
    let returnValue = '';
    if(options?.length > 0) {
      for (const option of options) {
        if (option[compareField] === givenValue) {
          returnValue = option[returnField];
          break;
        }
      }
    }
    return returnValue;
  }

  public getPlanIconClass(planStatus: any) {
    let cssClass = '';
    switch (planStatus) {
      case 'Active':
        cssClass = 'icon-accept cc_green_icon';
        break;

      case 'Retired':
        cssClass = 'icon-cancel';
        break;

      case 'Draft':
        cssClass = 'icon-write-new';
        break;

      case 'Operational':
        cssClass = 'icon-accept operational';
        break;

      case 'Test':
        cssClass = 'icon-document-text test';
        break;
    }
    return cssClass;
  }

  public sendHierarchy(params: any) {
    this.hierarchySubject.next({ text: params });
  }

  public getHierarchy(): Observable<any> {
    return this.hierarchySubject.asObservable();
  }

  public getFlagValuesFromId(flagRule: any) {
    const selectedFlags: any = [];

    // handle both old and new shapes for ng-multiselectdropdown
    const flagValuesArray = Array.isArray(flagRule.flagValue)
      ? flagRule.flagValue                   // new shape: [{id, name}, ...]
      : (flagRule.flagValue?.id || []);      // old shape: { id: [1,2,3] }

    flagValuesArray.forEach((element: any) => {
      const id = element?.id || element; // supports both object and primitive
      const item = flagRule.flagValues?.find((flag: any) => flag.id == id);
      if (item) {
        selectedFlags.push(item);
      }
    });

    return selectedFlags;
  }


  public setTranslatedObj(resp: any) {
    localStorage.setItem('languageTranslations', JSON.stringify(resp));
    this.translatedJSON = resp;
  }

  public getTranslationsOfSelectedLanguage(languageCode: any) {
    const url = this.translationsUrl + `/getAllLanguageTranslationsByLocaleAndSourceType?locale=${languageCode}&sourceType=ui`
    return this.http.get(url).toPromise();
  }

  public getTranslatedVal(key1: any) {
    const langTrans: any = localStorage.getItem('languageTranslations');
    this.translatedJSON = this.translatedJSON ? this.translatedJSON : JSON.parse(langTrans);
    const transObj = this.translatedJSON;
    const obj = _.find(transObj, (ele: any) => {
      return ele.key == key1
    });
    return obj ? obj.value : key1;
  }

  public disableDatesByGivenDate(milliseconds: any, type: any, days: any) {
    const givenDate = new Date(milliseconds);
    if (type == 'increaseDates') {
      givenDate.setDate(givenDate.getDate() + days);
    } else {
      givenDate.setDate(givenDate.getDate() - days);
    }
    return givenDate;
  }

  public getHistoricalData(payload: any, startRecord: any, recordsSize: any, periodPosition: any) {
    const filterURL =
      environment.host +
      '/participant/historicalData' +
      '?pageMaxId=' +
      startRecord +
      '&currentPeriod=' +
      (periodPosition == undefined ? true : periodPosition) +
      '&size=' +
      recordsSize;
    return this.http.post(filterURL, payload).pipe(map((response) => response));
  }

  public getHistoricalReportDetails() {
    const url = environment.host + '/report/HistoricalReport';
    return this.http.get(url).toPromise();
  }

  public loadTranslations() {
    const userLang = localStorage.getItem('userLanguage') || 'en_us',
      companyCode = this.getCompanyCode();
    const comTransCode: any = COMPANY_TRANSLATION_CODES;
    const companyLangCode = comTransCode[companyCode];
    if (companyLangCode) {
      /* Retriving Data from Client local file */
      this.translate.getTranslation(userLang).subscribe((mainres) => {
        localStorage.setItem('clientTranslationSetting', 'true');
        // localStorage.setItem('transLoad', "true");
        // this.transSubject.next(localStorage.getItem('transLoad'));
        this.translate.getTranslation(companyLangCode).subscribe((clientres) => {
          setTimeout(
             () => {
              Object.assign(mainres, clientres);
              this.translate.setTranslation(userLang, mainres, true);
              localStorage.setItem('transLoad', 'true');
              this.transSubject.next(localStorage.getItem('transLoad'));
            },
            1000
          );
        });
      });
    } else {
      setTimeout(
        () => {
          this.translate.use(userLang);
          localStorage.setItem('transLoad', 'true');
          this.transSubject.next(localStorage.getItem('transLoad'));
        },
        1000
      );
    }

    /*For aaproach2: Comment above code and uncomment below code */
    // const userLang = localStorage.getItem('userLanguage') || 'en';
    // this.translate.use(userLang);
  }

  public getCalculatedResponsorDate(payload: any) {
    const url = environment.host + '/participant/calcResponsorDate';
    return this.http.post(url, payload).toPromise();
  }

  public setEffectiveDate() {
    return this.prepareDateObj(new Date());
  }

  public applyFiltersBySourceType(arrayList: any, sourceType: any) {
    if (arrayList) {
      return arrayList.filter((ele: any) => {
        ele.reportSource = ele.reportSource || [];
        const sourceTypes = ele.reportSource.map((source: any) => {
          return source.name;
        });
        if (ele.filters && ele.filters.length) {
          ele.filters = this.applyFiltersBySourceType(ele.filters, sourceType) || [];
        }
        return !ele.reportSource || !ele.reportSource.length || sourceTypes.includes(sourceType.name);
      });
    }
  }

  public applyColumnsBySourceType(arrayList: any, sourceType: any) {
    if (arrayList) {
      return arrayList.filter((ele: any) => {
        ele.reportSource = ele.reportSource || [];
        const sourceTypes = ele.reportSource.map(function (source: any) {
          return source.name;
        });
        if (ele.columns && ele.columns.length) {
          ele.columns = this.applyColumnsBySourceType(ele.columns, sourceType) || [];
        }
        if (ele.columnSubGroups && ele.columnSubGroups.length) {
          ele.columnSubGroups = this.applyColumnsBySourceType(ele.columnSubGroups, sourceType) || [];
          if (ele.columnSubGroups.columns && ele.columnSubGroups.columns.length) {
            ele.columnSubGroups.columns = this.applyColumnsBySourceType(ele.columnSubGroups.columns, sourceType) || [];
          }
        }
        return !ele.reportSource || !ele.reportSource.length || sourceTypes.includes(sourceType.name);
      });
    }
  }

  public getPlanPeriod(regCode?: string) {
    if (regCode) {
      return this.http.get(this.url.toString() + '/period?regionCode=' + regCode).toPromise();
    } else {
      return this.http.get(this.url.toString() + '/period').toPromise();
    }
  }

  public setUpdateChangeSponsorScheduledJobs(value: string) {
    this.changeSponsorState.next(value);
  }

  public getUpdateChangeSponsorScheduledJobs(): Observable<any> {
    return this.changeSponsorState.asObservable();
  }

  public setUpdateChangeRecruiter(Object: any) {
    this.changeRecruiterState.next(Object);
  }

  public getUpdateChangeRecruiter(): Observable<any> {
    return this.changeRecruiterState.asObservable();
  }

  public setUpdateChangeOrgUnitList(result: any) {
    this.changeOrganizationUnit.next(result);
  }

  public getUpdateChangeOrgUnitList(): Observable<any> {
    return this.changeOrganizationUnit.asObservable();
  }

  public getParticipantAndDownlineDetails(clientID: string) {
    const url = environment.host + '/getParticipantAndDownlineDetails/' + clientID;
    return this.http.get(url).toPromise();
  }

  public setUpdateChangeParentOrgUnitList(result: any) {
    this.changeParentOrganizationUnit.next(result);
  }

  public getUpdateChangeParentOrgUnitList(): Observable<any> {
    return this.changeParentOrganizationUnit.asObservable();
  }

  public downloadReportAudit(reportName: string, count: number, reportType?: any) {
    let url = environment.host + '/audit/downloadReport';
    if (reportType != undefined) url += `?reportType=${reportType}`;
    const payload = {
      reportName: reportName,
      downloadCount: count
    };
    return this.http.post(url, payload).toPromise();
  }

  // check if the current report page is in default page or not
  public isDefaultView() {
    const url = this.router.url;
    const urlA = url.split('/');
    const defaultCheck = _.contains(urlA, 'default');
    return defaultCheck;
  }

  /**
   * Converts a number into currency format for a given location, based on locale code and currency code.
   * Example, for Poland, for a input number of 1234000.5, the output would be "1 234 000,50 zł"
   *
   * Locale code for Poland is "pl-PL", and currency code for Poland is "PLN"
   */
  public formatCurrency(amount: number): string {
    if (isNaN(amount)) return '';

    try {
      const code = CommonService.localeCurrency;
      const locale = CommonService.localeCode;
      // Custom formatting for Spain
      if (locale === 'es-ES' || locale === 'ES') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: code, // EUR
          currencyDisplay: 'symbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true
        }).format(amount);
      } else{
        return amount.toLocaleString(locale, {
        style: 'currency',
        currency: code,
        currencyDisplay: code === 'SGD' ? 'code' : 'symbol',
        useGrouping: true
      });
      }
    } catch {
      return String(amount);
    }
  }

  /**
   * Converts input value into a number format for the given location.
   * Example, for Poland, for an input number of 1234000.5, the output would be "1 234 000,5".
   * The locale code for Poland is "pl-PL"
   *
   */
  public formatValue(value: any): string {

    let locale = CommonService.localeCode;

    //when locale is undefined, take the locale value from userDetails.region.code
    //this is to fix the issue when in some places, where locale value isn't appearing the first time on login (without refresh)
    //Todo - review / refactor
    if(!locale){
      const usrDet: any = localStorage.getItem('userDetails');
      const userDetails = JSON.parse(usrDet);
      locale = userDetails && userDetails.region && userDetails.region.code;
    }

    if(value == undefined || value === '') return '';

    //if input value is text, return the same text
    // if(isNaN(value)) return value + '';

    if (typeof value === 'string' && isNaN(Number(value))) {
      return value;
    }

    // If input value is text, remove commas and convert to number
    if (typeof value === 'string') {
      value = Number(value.replace(/,/g, ''));
    }

    // Skip formatting for es-ES
    if (locale === 'es-ES' || locale ==='ES') {
      return Number(value).toString();
    }
   
    let result: string;

    try{
      result = Number(value).toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: true
      });
    } catch(e){
      result = Number(value).toLocaleString(LOCALE.defaultCode, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: true
      });
    }
    return result.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  //VCE-27296
  public setAppVersion(appVersion: any) {
    localStorage.setItem('appVersion', appVersion);
  }

  public getAppVersion() {
    const av = localStorage.getItem('appVersion');
    return av;
  }

  public addCustomErrorMessage(userMessage: string, errorMsg: string): string {
    // Logic to add the custom error message
    if (!userMessage.includes(errorMsg)) {
      userMessage += ` ${errorMsg}`;
    }
    return userMessage;
  }

  public removeCustomErrorMessage(userMessage: string, errorMsg: string): string {
    if (userMessage.includes(errorMsg)) {
      userMessage = userMessage.replace(` ${errorMsg}`, '');
    }
    return userMessage;
  }

  //VCE-27296 - end

  /**
   * rounding a number to two digits
   * @param number
   * @returns rounded value of number
   */
  public round(number: number){
    return Number(number.toFixed(2));
  }

  /**
   * preparing a error message
   * @param validationErrorMsg is the error message to be prepared & to return
   * @param obj is the object holding following 3 proeprties
   * findInErrorMsg is the boolean which specifies whether the errorMsg is present in item error or not,
   * errorMsg (message which we get for the input field from directive),
   * itemErrorMsg (it is the message held by the particular input field)
   * @returns framed errorMessage (validationErrorMsg)
   */
  public twoDecimalErrorMessage(validationErrorMsg: any, obj: any){
    let {findInErrorMsg, errorMsg, itemErrorMsg} = obj;
    // check if the errorMsg from the directive is not empty
    if (errorMsg != '') {
      // check if the errorMsg is not present in the ValidationErrorMsg
      if (!findInErrorMsg) {
        validationErrorMsg = validationErrorMsg ?
          (itemErrorMsg ? validationErrorMsg.replace(itemErrorMsg, errorMsg) : validationErrorMsg + errorMsg) :
          errorMsg;
      } else {
        validationErrorMsg = validationErrorMsg;
      }
    } else if (errorMsg == '') {  // check if the erroMsg is empty
      // check if the errorMsg is already present in the itemErrorMsg
      // if present replace itemErrorMsg with ''
      // else return as it is
      if (itemErrorMsg) {
        validationErrorMsg = validationErrorMsg.replace(itemErrorMsg, '');
      } else {
        validationErrorMsg = validationErrorMsg;
      }
    }
    return validationErrorMsg.trim().length > 0 ? validationErrorMsg : '';
  }

  public getAgGridLicenseKey(): Observable<any> { 
      return this.http.get(this.licenseUrl);
    }
	
  public terminateIncentiveProgram(clientId: any, incentivePlanId: any){
    const url = environment.host +
      '/incentive/cancelProgram' +
      '?clientID=' + clientId +
      '&incentivePlanId='+ incentivePlanId;
    return this.http.post(url, {}).toPromise();
  }
  
  public validateUser(emailId: any) {
      return this.http.post(this.validUserUrl, emailId).toPromise();
    }
}
